/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __spreadArrays() {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
}

function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function isStateNode(node) {
  return 'parent' in node;
}

function generateUUID() {
  var d = new Date().getTime();
  d += new Date().valueOf();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : r & 0x3 | 0x8).toString(16);
  });
}

function generateTimeStamp() {
  return new Date().getTime();
}

var diff = require('deep-diff').diff;

function deepDiff(obj1, obj2) {
  return diff(obj1, obj2);
}

function createProvenanceGraph(state) {
  var _a;

  var root = {
    id: generateUUID(),
    label: 'Root',
    metadata: {
      createdOn: generateTimeStamp(),
      type: 'Root'
    },
    children: [],
    state: state
  };
  var graph = {
    nodes: (_a = {}, _a[root.id] = root, _a),
    root: root.id,
    current: root.id
  };
  return graph;
}

function _goToNode(graph, id) {
  var newGraph = deepCopy(graph);
  var newCurrentNode = graph.nodes[id];

  if (!newCurrentNode) {
    throw new Error('No such node exists');
  }

  newGraph.current = newCurrentNode.id;
  return newGraph;
}

function importState(graph, initalState, importedState) {
  var newGraph = deepCopy(graph);

  var createNewStateNode = function createNewStateNode(parent, state, diffs) {
    return {
      id: generateUUID(),
      label: 'Imported state',
      metadata: {
        createdOn: generateTimeStamp()
      },
      artifacts: {
        extra: [],
        diffs: diffs
      },
      parent: parent,
      children: [],
      state: state
    };
  };

  var diffs = deepDiff(initalState, importedState);
  var newNode = createNewStateNode(graph.current, importedState, diffs);
  newGraph.nodes[newNode.id] = newNode;
  newGraph.nodes[newGraph.current].children.push(newNode.id);
  newGraph.current = newNode.id;
  return newGraph;
}

function _addExtraToNodeArtifact(graph, id, extra) {
  var newGraph = deepCopy(graph);
  var node = newGraph.nodes[id];

  if (isStateNode(node)) {
    node.artifacts.extra.push({
      time: generateTimeStamp(),
      e: extra
    });
  } else {
    throw new Error('Cannot add artifacts to Root Node');
  }

  return newGraph;
}

function _getExtraFromArtifact(graph, id) {
  var node = graph.nodes[id];

  if (isStateNode(node)) {
    return node.artifacts.extra || [];
  }

  throw new Error('Root does not have artifacts');
}

function applyActionFunction(graph, label, action, args, metadata, artifacts) {
  if (artifacts === void 0) {
    artifacts = {};
  }

  var newGraph = deepCopy(graph);
  var currentId = newGraph.current;
  var currentState = deepCopy(newGraph.nodes[currentId].state);

  var createNewStateNode = function createNewStateNode(parent, state, diffs) {
    return {
      id: generateUUID(),
      label: label,
      metadata: _assign({
        createdOn: generateTimeStamp()
      }, metadata),
      artifacts: _assign({
        diffs: diffs,
        extra: []
      }, artifacts),
      parent: parent,
      children: [],
      state: state
    };
  };

  var newState = args ? action.apply(void 0, __spreadArrays([currentState], args)) : action(currentState);
  var parentId = graph.current;
  var diffs = deepDiff(graph.nodes[currentId].state, newState);
  var newNode = createNewStateNode(parentId, newState, diffs);
  newGraph.nodes[newNode.id] = newNode;
  newGraph.nodes[currentId].children.push(newNode.id);
  newGraph.current = newNode.id; // console.log(JSON.stringify(newGraph, null, 2));

  return newGraph;
}

var GLOBAL = 'GLOBAL';
var ARTIFACT = 'ARTIFACT';

function initEventManager() {
  var eventRegistry = {};
  var artifactEventRegistry = {};

  function callGlobalEvents(state) {
    if (eventRegistry[GLOBAL] && eventRegistry[GLOBAL].length > 0) {
      eventRegistry[GLOBAL].forEach(function (f) {
        return f(state);
      });
    }
  }

  function callArtifactEvents(node) {
    if (artifactEventRegistry[ARTIFACT] && artifactEventRegistry[ARTIFACT].length > 0) {
      if (isStateNode(node)) {
        if (node.artifacts.extra) {
          var extra_1 = node.artifacts.extra || [];
          artifactEventRegistry[ARTIFACT].forEach(function (f) {
            return f(extra_1);
          });
        }
      }
    }
  }

  return {
    addGlobalObserver: function addGlobalObserver(func) {
      if (!eventRegistry[GLOBAL]) {
        eventRegistry[GLOBAL] = [];
      }

      eventRegistry[GLOBAL].push(func);
    },
    addObserver: function addObserver(propPath, func) {
      var path = propPath.join('|');

      if (!eventRegistry[path]) {
        eventRegistry[path] = [];
      }

      eventRegistry[path].push(func);
    },
    addArtifactObserver: function addArtifactObserver(func) {
      if (!artifactEventRegistry[ARTIFACT]) {
        artifactEventRegistry[ARTIFACT] = [];
      }

      artifactEventRegistry[ARTIFACT].push(func);
    },
    callEvents: function callEvents(diffs, state, node) {
      if (isStateNode(node)) {
        callArtifactEvents(node);
      }

      if (diffs.length === 0) return;
      callGlobalEvents(state);
      var diffStrings = [];
      diffs.forEach(function (diff) {
        var pathArr = diff.path;
        var changedPaths = [];
        var diffStr = pathArr.join('|');

        if (!diffStrings.includes(diffStr)) {
          diffStrings.push(diffStr);
        } else {
          return;
        }

        pathArr.forEach(function (path) {
          if (changedPaths.length === 0) {
            changedPaths.push(path);
          } else {
            changedPaths.push([changedPaths.reverse()[0], path].join('|'));
          }
        });
        changedPaths.reverse().forEach(function (cp) {
          if (eventRegistry[cp]) {
            eventRegistry[cp].forEach(function (f) {
              return f(state);
            });
          }
        });
      });
    }
  };
}

var decompressFromEncodedURIComponent = require('lz-string').decompressFromEncodedURIComponent;

var compressToEncodedURIComponent = require('lz-string').compressToEncodedURIComponent;

function initProvenance(initialState, loadFromUrl) {
  if (loadFromUrl === void 0) {
    loadFromUrl = false;
  }

  var _graph = createProvenanceGraph(initialState);

  var initalStateRecord = deepCopy(initialState);
  var EM = initEventManager();
  var surroundChars = '||';

  function loadUrl() {
    if (!window || !window.location || !window.location.href) {
      throw new Error('Window and/or location not defined. Make sure this is a browser environment.');
    }

    var url = window.location.href;

    if (!url.includes('||')) {
      return;
    }

    var importString = url.split('||').reverse()[0];
    var importedState = JSON.parse(decompressFromEncodedURIComponent(importString));
    importStateAndAddNode(importedState);
  }

  function curr() {
    return _graph.nodes[_graph.current];
  }

  function triggerEvents(oldState) {
    var currentState = _graph.nodes[_graph.current].state;
    var diffs = deepDiff(oldState, currentState);
    EM.callEvents(diffs || [], currentState, curr());
  }

  function importStateAndAddNode(state) {
    _graph = importState(_graph, initalStateRecord, state);
  }

  return {
    graph: function graph() {
      return deepCopy(_graph);
    },
    current: function current() {
      return deepCopy(_graph.nodes[_graph.current]);
    },
    root: function root() {
      return deepCopy(_graph.nodes[_graph.root]);
    },
    applyAction: function applyAction(label, action, args, metadata, artifacts, eventType) {
      if (metadata === void 0) {
        metadata = {};
      }

      var oldState = deepCopy(_graph.nodes[_graph.current].state);

      if (eventType) {
        metadata.type = eventType;
      }

      _graph = applyActionFunction(_graph, label, action, args, metadata, artifacts);
      triggerEvents(oldState);
      return _graph.nodes[_graph.current].state;
    },
    goToNode: function goToNode(id) {
      var oldState = deepCopy(_graph.nodes[_graph.current].state);
      _graph = _goToNode(_graph, id);
      triggerEvents(oldState);
    },
    addExtraToNodeArtifact: function addExtraToNodeArtifact(id, extra) {
      _graph = _addExtraToNodeArtifact(_graph, id, extra);
      EM.callEvents([], _graph.nodes[id].state, _graph.nodes[id]);
    },
    getExtraFromArtifact: function getExtraFromArtifact(id) {
      return _getExtraFromArtifact(_graph, id);
    },
    goBackOneStep: function goBackOneStep() {
      var oldState = deepCopy(_graph.nodes[_graph.current].state);
      var current = _graph.nodes[_graph.current];

      if (isStateNode(current)) {
        _graph = _goToNode(_graph, current.parent);
      } else {
        throw new Error('Already at root');
      }

      triggerEvents(oldState);
    },
    goBackNSteps: function goBackNSteps(n) {
      var oldState = deepCopy(_graph.nodes[_graph.current].state);
      var num = n;
      var tempGraph = deepCopy(_graph);

      while (n > 0) {
        var current = tempGraph.nodes[tempGraph.current];

        if (isStateNode(current)) {
          tempGraph = _goToNode(_graph, current.parent);
        } else {
          throw new Error("Cannot go back " + num + " steps. Reached root after " + (num - n) + " steps");
        }

        n--;
      }

      _graph = tempGraph;
      triggerEvents(oldState);
    },
    goForwardOneStep: function goForwardOneStep() {
      var oldState = deepCopy(_graph.nodes[_graph.current].state);
      var current = _graph.nodes[_graph.current];

      if (current.children.length > 0) {
        _graph = _goToNode(_graph, current.children.reverse()[0]);
      } else {
        throw new Error('Already at the latest node in this branch');
      }

      triggerEvents(oldState);
    },
    reset: function reset() {
      var oldState = deepCopy(_graph.nodes[_graph.current].state);
      _graph = _goToNode(_graph, _graph.root);
      triggerEvents(oldState);
    },
    done: function done() {
      if (loadFromUrl) {
        var oldState = deepCopy(_graph.nodes[_graph.current].state);
        loadUrl();
        triggerEvents(oldState);
      }
    },
    addObserver: function addObserver(propPath, func) {
      var state = _graph.nodes[_graph.current].state;
      var path = state;
      propPath.forEach(function (prop) {
        var keys = Object.keys(path);
        if (!keys.includes(prop)) throw new Error("Path " + propPath.join('.') + " does not exist");
        path = path[prop];
      });
      EM.addObserver(propPath, func);
    },
    addGlobalObserver: function addGlobalObserver(func) {
      EM.addGlobalObserver(func);
    },
    addArtifactObserver: function addArtifactObserver(func) {
      EM.addArtifactObserver(func);
    },
    exportState: function exportState(partial) {
      if (partial === void 0) {
        partial = false;
      }

      var exportedState = {};
      var currentState = _graph.nodes[_graph.current].state;

      if (partial) {
        Object.keys(currentState).forEach(function (key) {
          var _a;

          var prev = initalStateRecord[key];
          var curr = currentState[key];

          if (JSON.stringify(prev) !== JSON.stringify(curr)) {
            exportedState = _assign(_assign({}, exportedState), (_a = {}, _a[key] = currentState[key], _a));
          }
        });
      } else {
        exportedState = _assign({}, currentState);
      }

      var exportedStateObject = exportedState;
      var compressedString = compressToEncodedURIComponent(JSON.stringify(exportedStateObject));
      return "" + surroundChars + compressedString;
    },
    importState: function importState(importString) {
      var oldState = deepCopy(_graph.nodes[_graph.current].state);
      var importedStates = JSON.parse(decompressFromEncodedURIComponent(importString.replace('||', '')));

      var state = _assign(_assign({}, _graph.nodes[_graph.current].state), importedStates);

      importStateAndAddNode(state);
      triggerEvents(oldState);
    },
    exportProvenanceGraph: function exportProvenanceGraph() {
      return JSON.stringify(_graph);
    },
    importProvenanceGraph: function importProvenanceGraph(importString) {
      var oldState = deepCopy(_graph.nodes[_graph.current].state);
      _graph = JSON.parse(importString);
      triggerEvents(oldState);
    }
  };
}

export { initProvenance, isStateNode };